import { format, parseISO } from "date-fns";

import { translationService } from "../translation/TranslationService";
import { utcToZonedTime, format as formatTz } from "date-fns-tz";

export interface IDate {
  currentDate: Date;
  formatDate: (date: Date | string) => string;
  dateToUTC: (date: Date) => Date;
}

export class DateService implements IDate {
  currentDate: Date = new Date();
  // Turn an API timestamp into a real instant. The signature has always allowed a Date, but
  // only ISO STRINGS actually worked. A Date stringifies to "Tue Aug 12 2026 10:23:45 GMT+0200
  // (Central European Summer Time)", which does not end in an offset — so the branch below
  // appended "Z" and parseISO returned an Invalid Date, reaching the caller as
  // "RangeError: Invalid time value". A Date is already an absolute instant, so take it as-is.
  //
  // Returns undefined instead of an Invalid Date so callers can tell "no usable date" from a
  // date they can do arithmetic on. Anything deriving a date FROM an API timestamp must come
  // through here: `new Date("2026-08-18T07:09:21")` is read as LOCAL time by the browser, which
  // silently shifts every derived date by the viewer's offset.
  parseApiDateTime = (date: string | Date): Date | undefined => {
    let utcDate: Date;
    if (date instanceof Date) {
      utcDate = date;
    } else {
      // A timestamp with no zone is UTC (that is what the API sends); anything already carrying
      // Z or an offset is left alone.
      const trimmed = date.toString().replace(/(\.\d{3})\d+$/, "$1");
      const isoWithZone = /[Zz]|[+\-]\d{2}(:?\d{2})?$/.test(trimmed) ? trimmed : trimmed + "Z";
      utcDate = parseISO(isoWithZone);
    }
    return isNaN(utcDate.getTime()) ? undefined : utcDate;
  }

  formatDateTime = (date: string | Date): string => {
    const utcDate = this.parseApiDateTime(date);

    // Render nothing rather than throwing: this runs inside render passes, where an exception
    // takes the whole page down instead of just the one date.
    if (!utcDate) return "";

    var timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    var dateTimeWithZone = utcToZonedTime(new Date(utcDate), timezone);
    return formatTz(dateTimeWithZone, "MM/dd/yyyy HH:mm:ss", { timeZone: timezone });
  }

  formatDate = (date: string | Date | undefined, formatString: string = "P"): string => {
    if (!date) return "";

    const dateObject = typeof date === "string" ? new Date(date) : date;

    const formattedDate = format(dateObject, formatString, {
      locale: translationService.locale,
    });

    return formattedDate;
  };

  formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hrs, mins, secs].map((unit) => String(unit).padStart(2, "0")).join(":");
  };

  dateToUTC = (date: Date): Date => {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  };

  getDateBasedOnBMFormat = (date: string, formatString: string): Date | null => {
    if (!date) return null;
    if (formatString === "yyyyMMdd") {
      const replacedDate = date.replace(/(\d{4})(\d{2})(\d{2})/g, "$1/$2/$3");
      return new Date(replacedDate);
    }
    return new Date(date);
  };

  getYearFromDate = (date: string): number | string => {
    return new Date(date).getFullYear();
  };

  dateIsInInterval = (
    date: Date | string,
    startIntervalDate: Date | string,
    endIntervalDate: Date | string
  ): boolean => {
    let dateToCheckObject = date;
    let startIntervalDateObject = startIntervalDate;
    let endIntervalDateObject = endIntervalDate;

    if (typeof date === "string") {
      dateToCheckObject = new Date(date);
    }

    if (typeof startIntervalDate === "string") {
      startIntervalDateObject = new Date(startIntervalDate);
    }

    if (typeof endIntervalDate === "string") {
      endIntervalDateObject = new Date(endIntervalDate);
    }

    return (
      startIntervalDateObject <= dateToCheckObject && dateToCheckObject <= endIntervalDateObject
    );
  };

  getDateFromString = (date: string | Date): Date => {
    const fullDate = typeof date === "string" ? parseISO(date) : parseISO(date.toISOString());
    return this.dateToUTC(fullDate);
  };
}

export const dateService = new DateService();
export default dateService;
